import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'motion/react';
import { ArrowLeft, Pill } from 'lucide-react';
import { StorylineCards } from '@/components/StorylineCards';
import { StorylineSectorChart } from '@/components/StorylineSectorChart';
import { StorylineStockChartsGrid } from '@/components/StorylineStockChartsGrid';
import { Glp1StorylineEventsPanel } from '@/components/Glp1StorylineEventsPanel';
import {
  MARKET_STORYLINES,
  getStorylineById,
  type MarketStoryline,
} from '@/mocks/marketStorylinesMock';

function directionBadgeClass(direction: string | undefined): string {
  switch (direction?.toLowerCase()) {
    case 'bullish':
      return 'bg-emerald-50 text-emerald-800';
    case 'bearish':
      return 'bg-rose-50 text-rose-800';
    case 'mixed':
      return 'bg-violet-50 text-violet-800';
    default:
      return 'bg-amber-50 text-amber-800';
  }
}

function Glp1DisruptionDetail({ storyline }: { storyline: MarketStoryline }) {
  return <Glp1StorylineEventsPanel storyline={storyline} />;
}

function StorylineDetail({ storyline }: { storyline: MarketStoryline }) {
  if (storyline.id === 'glp1-disruption') {
    return <Glp1DisruptionDetail storyline={storyline} />;
  }

  const hasStockCharts = Boolean(storyline.chartSymbols?.length);

  return (
    <article className={`mx-auto ${hasStockCharts ? 'max-w-6xl' : 'max-w-4xl'}`}>
      <div className="mb-6 flex flex-wrap items-center gap-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-600 text-sm font-medium text-white">
          {storyline.rank}
        </span>
        <span
          className={`rounded-lg px-2.5 py-0.5 text-xs capitalize ${directionBadgeClass(storyline.direction)}`}
        >
          {storyline.direction}
        </span>
        {storyline.tickers?.map((ticker) => (
          <span
            key={ticker}
            className="rounded-lg bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600"
          >
            {ticker}
          </span>
        ))}
      </div>

      <h1 className="mb-4 text-3xl tracking-tight text-gray-900">{storyline.title}</h1>

      <p className="mb-8 text-base leading-relaxed text-gray-600">{storyline.summary}</p>

      {storyline.chartSymbols?.length ? (
        <StorylineStockChartsGrid
          charts={storyline.chartSymbols}
          fallbackEvents={storyline.events}
          storylineId={storyline.id}
          title={`${storyline.title} — key names`}
          className="mb-8"
        />
      ) : (
        <StorylineSectorChart
          chartSymbol={storyline.chartSymbol}
          chartLabel={storyline.chartLabel}
          events={storyline.events}
          className="mb-8"
        />
      )}

      <div className="rounded-2xl border border-gray-200 bg-white p-6">
        <div className="space-y-4 text-sm leading-relaxed text-gray-700">
          {storyline.body.split('\n\n').map((paragraph) => (
            <p key={paragraph.slice(0, 48)}>{paragraph}</p>
          ))}
        </div>
      </div>

      {storyline.tags?.length ? (
        <div className="mt-6 flex flex-wrap gap-2">
          {storyline.tags.map((tag) => (
            <span
              key={tag}
              className="rounded-lg bg-gray-100 px-3 py-1 text-xs text-gray-600"
            >
              {tag}
            </span>
          ))}
        </div>
      ) : null}
    </article>
  );
}

export function StorylinesPage() {
  const navigate = useNavigate();
  const { id: idParam } = useParams<{ id?: string }>();

  const selectedStoryline = useMemo(() => {
    if (!idParam) return null;
    return getStorylineById(idParam) ?? null;
  }, [idParam]);

  const isDetailView = Boolean(idParam);
  const isGlp1Detail = selectedStoryline?.id === 'glp1-disruption';

  return (
    <div className={`min-h-screen bg-gray-50 ${isGlp1Detail ? 'flex flex-col' : ''}`}>
      <motion.header
        initial={isGlp1Detail ? { opacity: 0, y: -20 } : undefined}
        animate={isGlp1Detail ? { opacity: 1, y: 0 } : undefined}
        transition={isGlp1Detail ? { duration: 0.5 } : undefined}
        className={`border-b border-gray-200 bg-white ${isGlp1Detail ? 'shrink-0' : ''}`}
      >
        <div className="mx-auto flex max-w-7xl items-center gap-4 px-6 py-4">
          <button
            type="button"
            onClick={() => (isDetailView ? navigate('/market/storylines') : navigate('/dashboard'))}
            className="rounded-lg p-2 transition-colors hover:bg-gray-100"
            aria-label={isDetailView ? 'Back to storylines' : 'Back to dashboard'}
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          {isGlp1Detail ? (
            <div className="flex min-w-0 flex-1 items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gray-900">
                <Pill className="h-5 w-5 text-white" />
              </div>
              <div className="min-w-0">
                <h2 className="tracking-tight text-gray-900">{selectedStoryline?.title}</h2>
                <p className="truncate text-sm text-gray-500">{MARKET_STORYLINES.as_of_label}</p>
              </div>
            </div>
          ) : (
            <div>
              <h1 className="text-gray-900">
                {isDetailView && selectedStoryline ? selectedStoryline.title : 'Storylines'}
              </h1>
              <p className="text-sm text-gray-500">{MARKET_STORYLINES.as_of_label}</p>
            </div>
          )}
        </div>
      </motion.header>

      <main
        className={
          isGlp1Detail
            ? 'mx-auto w-full max-w-7xl flex-1 px-6 py-8 md:py-10'
            : 'mx-auto max-w-7xl px-6 py-10'
        }
      >
        {isDetailView && !selectedStoryline ? (
          <div className="rounded-2xl border border-gray-200 bg-white p-12 text-center">
            <p className="text-gray-500">Storyline not found.</p>
            <button
              type="button"
              onClick={() => navigate('/market/storylines')}
              className="mt-4 text-sm text-gray-600 underline hover:text-gray-900"
            >
              View all storylines
            </button>
          </div>
        ) : isDetailView && selectedStoryline ? (
          <StorylineDetail storyline={selectedStoryline} />
        ) : (
          <>
            <p className="mb-8 max-w-2xl text-gray-600">
              Ten narratives shaping markets right now — AI, geopolitics, macro policy, credit,
              healthcare, autos, semiconductors, real estate, trade, and market concentration.
            </p>
            <StorylineCards
              storylines={MARKET_STORYLINES.storylines}
              onStorylineClick={(storyline) => navigate(`/market/storylines/${storyline.id}`)}
            />
          </>
        )}
      </main>
    </div>
  );
}
